import { useEffect, useState } from "react";
import { CalendarClock, X } from "lucide-react";

import { Button } from "@/design-system/primitives";
import { useRequestClinicianVisit } from "@/lib/api/clinicianHooks";
import type {
  PrescriptionWorkItem,
  VisitRequestReason,
} from "@/types/clinician";

export const VISIT_REQUEST_REASONS: ReadonlyArray<{
  value: VisitRequestReason;
  label: string;
}> = [
  { value: "discuss_dosing", label: "Discuss dosing" },
  { value: "side_effects", label: "Side effects or tolerability" },
  { value: "clinical_reassessment", label: "Clinical reassessment" },
  { value: "other", label: "Other" },
];

export function VisitRequestModal({
  item,
  onClose,
  onComplete,
}: {
  item: PrescriptionWorkItem;
  onClose: () => void;
  onComplete: (patientNotified: boolean) => void;
}) {
  const requestVisit = useRequestClinicianVisit(item.patient.id);
  const [reason, setReason] = useState<VisitRequestReason>("discuss_dosing");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !requestVisit.isPending) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, requestVisit.isPending]);

  const submit = async () => {
    setError(null);
    try {
      const response = await requestVisit.mutateAsync({
        order_id: item.id,
        reason_code: reason,
        note: note.trim() || undefined,
      });
      onComplete(response.patient_notified);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "The visit request could not be recorded. Nothing was changed.",
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-ink/55 backdrop-blur-sm p-10 md:p-20 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="visit-request-title"
    >
      <section className="w-full max-w-[56rem] bg-page border border-line shadow-xl">
        <header className="px-15 py-13 border-b border-line-subtle flex items-start justify-between gap-10">
          <div>
            <p className="type-label-xs text-info">PATIENT VISIT</p>
            <h2 id="visit-request-title" className="type-heading-lg mt-4">
              Request a visit before prescribing
            </h2>
            <p className="type-body-sm text-ink-muted mt-4">
              {item.patient.display_name || item.patient.email} · {item.product}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close visit request"
            disabled={requestVisit.isPending}
            onClick={onClose}
            className="p-5 text-ink-muted hover:text-ink disabled:opacity-40"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        <div className="p-15 flex flex-col gap-12">
          <div className="border border-info/30 bg-info-wash p-10 flex gap-8">
            <CalendarClock size={16} className="text-info shrink-0 mt-1" />
            <p className="type-body-sm text-ink-secondary">
              The patient is asked by email and text to book a video visit
              with you. This prescription moves to Visit required until the
              visit starts, or until you record attendance with Patient
              attended. You can withdraw the request at any time.
            </p>
          </div>

          <label className="flex flex-col gap-5">
            <span className="type-label-xs text-ink-muted">VISIT REASON</span>
            <select
              aria-label="Visit request reason"
              value={reason}
              onChange={(event) =>
                setReason(event.target.value as VisitRequestReason)
              }
              className="min-h-40 border border-line bg-raised px-10 type-body-sm"
            >
              {VISIT_REQUEST_REASONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-5">
            <span className="type-label-xs text-ink-muted">
              NOTE FOR YOUR RECORD · OPTIONAL
            </span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What you want to cover in the visit. Kept on the prescription, not sent to the patient."
              className="border border-line bg-raised px-10 py-8 type-body-sm outline-none focus:shadow-focus resize-y"
            />
          </label>

          {error && (
            <div className="border border-danger/30 bg-danger-wash p-9 type-body-sm text-danger">
              {error}
            </div>
          )}
        </div>

        <footer className="px-15 py-11 border-t border-line-subtle flex flex-wrap items-center justify-end gap-8">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={requestVisit.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={requestVisit.isPending}
            onClick={() => void submit()}
          >
            Request visit
          </Button>
        </footer>
      </section>
    </div>
  );
}
